import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../../context/auth'
import { useAgents } from '../../context/agents'
import { PageTitleService } from '../../hooks/usePageTitle'
import { chatApi } from '../../services/chatService'
import { STATUS_COLORS, STATUS_LABELS } from '../../constants/statusColors'
import { KanbanColumn } from './components/KanbanColumn'
import { KanbanFilters } from './components/KanbanFilters'
import type { SessionSummary, StatusColumnConfig } from './components/types'

export default function HomeDashboard() {
  const { permissions } = useAuth()
  const { agents, isLoading: agentsLoading } = useAgents()
  const [error, setError] = useState<string | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [statusUpdating, setStatusUpdating] = useState<string | null>(null)
  const [collapsedCancelled, setCollapsedCancelled] = useState(true)
  const [pageSize] = useState(5)
  const [pageByStatus, setPageByStatus] = useState<Record<string, number>>({
    in_progress: 1,
    waiting: 1,
    completed: 1,
    cancelled: 1,
  })
  const [recentlyMoved, setRecentlyMoved] = useState<Record<string, string>>({})
  const [agentFilter, setAgentFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('7d')
  const navigate = useNavigate()

  useEffect(() => {
    PageTitleService.setTitle('Kanban')
  }, [])

  useEffect(() => {
    setPageByStatus({
      in_progress: 1,
      waiting: 1,
      completed: 1,
      cancelled: 1,
    })
  }, [agentFilter, dateFilter, sessions.length])

  useEffect(() => {
    const loadData = async () => {
      if (agentsLoading) return
      setError(null)

      try {
        const allowedAgents = agents.filter(
          agent =>
            agent.status === 'active' &&
            (permissions?.allowedAgents?.includes(agent.type) ||
              permissions?.canManageUsers)
        )

        const results = await Promise.all(
          allowedAgents.map(async agent => {
            try {
              const data = await chatApi.listSessions(agent.type)
              return data.map(item => ({
                id: item.id,
                title: item.title,
                agentType: item.agentType,
                agentName: agent.name,
                messageCount: item.messageCount,
                timestamp: item.timestamp,
                status: item.status || 'in_progress',
                source: item.source ?? 'manual',
                metadata: item.metadata,
              }))
            } catch {
              return []
            }
          })
        )

        setSessions(results.flat())
      } catch {
        setError('Não foi possível carregar seu painel agora.')
      }
    }

    loadData()
  }, [
    agents,
    agentsLoading,
    permissions?.allowedAgents,
    permissions?.canManageUsers,
  ])

  const statusColumns: StatusColumnConfig[] = useMemo(
    () => [
      {
        id: 'in_progress',
        label: STATUS_LABELS.in_progress,
        tone: `${STATUS_COLORS.in_progress.activeBorder} ${STATUS_COLORS.in_progress.activeBg}`,
        pill: `${STATUS_COLORS.in_progress.dot} ${STATUS_COLORS.in_progress.text}`,
      },
      {
        id: 'waiting',
        label: STATUS_LABELS.waiting,
        tone: `${STATUS_COLORS.waiting.activeBorder} ${STATUS_COLORS.waiting.activeBg}`,
        pill: `${STATUS_COLORS.waiting.dot} ${STATUS_COLORS.waiting.text}`,
      },
      {
        id: 'completed',
        label: STATUS_LABELS.completed,
        tone: `${STATUS_COLORS.completed.activeBorder} ${STATUS_COLORS.completed.activeBg}`,
        pill: `${STATUS_COLORS.completed.dot} ${STATUS_COLORS.completed.text}`,
      },
      {
        id: 'cancelled',
        label: STATUS_LABELS.cancelled,
        tone: `${STATUS_COLORS.cancelled.activeBorder} ${STATUS_COLORS.cancelled.activeBg}`,
        pill: `${STATUS_COLORS.cancelled.dot} ${STATUS_COLORS.cancelled.text}`,
      },
    ],
    []
  )

  const groupedByStatus = useMemo(() => {
    const groups: Record<string, SessionSummary[]> = {}
    statusColumns.forEach(col => {
      groups[col.id] = []
    })

    const filtered = sessions.filter(session => {
      if (agentFilter !== 'all' && session.agentType !== agentFilter)
        return false

      if (dateFilter !== 'all') {
        const created = new Date(session.timestamp)
        const today = new Date()
        const startOfToday = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        )
        const startOfYesterday = new Date(startOfToday)
        startOfYesterday.setDate(startOfYesterday.getDate() - 1)

        const diffMs = startOfToday.getTime() - created.getTime()
        const diffDays = diffMs / (1000 * 60 * 60 * 24)

        if (dateFilter === 'today' && created < startOfToday) return false
        if (
          dateFilter === 'yesterday' &&
          (created < startOfYesterday || created >= startOfToday)
        )
          return false
        if (dateFilter === '7d' && diffDays > 7) return false
        if (dateFilter === '15d' && diffDays > 15) return false
        if (dateFilter === '30d' && diffDays > 30) return false
      }

      return true
    })

    filtered.forEach(session => {
      const status = statusColumns.find(col => col.id === session.status)
        ? session.status
        : 'in_progress'
      if (!groups[status]) groups[status] = []
      groups[status].push(session)
    })

    Object.values(groups).forEach(list =>
      list.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
    )
    return groups
  }, [sessions, statusColumns, agentFilter, dateFilter])

  const handleStatusChange = async (sessionId: string, newStatus: string) => {
    setStatusUpdating(sessionId)
    try {
      await chatApi.updateSessionStatus(sessionId, newStatus)
      setSessions(prev =>
        prev.map(s =>
          s.id === sessionId
            ? { ...s, status: newStatus, timestamp: new Date().toISOString() }
            : s
        )
      )
      setRecentlyMoved(prev => ({ ...prev, [sessionId]: newStatus }))
      setTimeout(() => {
        setRecentlyMoved(prev => {
          const next = { ...prev }
          delete next[sessionId]
          return next
        })
      }, 700)
    } catch {
      setError('Não foi possível atualizar o status da conversa.')
    } finally {
      setStatusUpdating(null)
    }
  }

  const handleNavigateToSession = (session: SessionSummary) => {
    navigate(`/agents/${session.agentType}/chat?sessionId=${session.id}`)
  }

  const handleLoadMore = (columnId: string) => {
    setPageByStatus(prev => ({
      ...prev,
      [columnId]: (prev[columnId] || 1) + 1,
    }))
  }

  const visibleColumns = statusColumns.filter(
    col => !(col.id === 'cancelled' && collapsedCancelled)
  )

  return (
    <div className="min-h-screen w-full">
      <div className="mt-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Kanban por status
            </h2>
          </div>
          <KanbanFilters
            agents={agents.map(a => ({ type: a.type, name: a.name }))}
            agentFilter={agentFilter}
            dateFilter={dateFilter}
            collapsedCancelled={collapsedCancelled}
            onAgentFilterChange={setAgentFilter}
            onDateFilterChange={setDateFilter}
            onToggleCancelled={() => setCollapsedCancelled(v => !v)}
          />
        </div>

        <div
          className={`grid grid-cols-1 gap-4 ${
            collapsedCancelled ? 'md:grid-cols-3' : 'md:grid-cols-4'
          }`}
        >
          {visibleColumns.map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              sessions={groupedByStatus[column.id] || []}
              columns={statusColumns}
              currentPage={pageByStatus[column.id] || 1}
              pageSize={pageSize}
              recentlyMoved={recentlyMoved}
              statusUpdating={statusUpdating}
              onStatusChange={handleStatusChange}
              onNavigate={handleNavigateToSession}
              onLoadMore={handleLoadMore}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-200 p-4">
          {error}
        </div>
      )}
    </div>
  )
}
